import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import BrandProfile from "@/pages/BrandProfile";
import NewCampaign from "@/pages/NewCampaign";
import CampaignDetail from "@/pages/CampaignDetail";
import CalendarPage from "@/pages/CalendarPage";
import SmartHooks from "@/pages/SmartHooks";
import Settings from "@/pages/Settings";
import Onboarding from "@/pages/Onboarding";
import OfferProfiles from "@/pages/OfferProfiles";
import OfferProfileBuilder from "@/pages/OfferProfileBuilder";
import PitchTest from "@/pages/PitchTest";
import IncomeMode from "@/pages/IncomeMode";
import NewProject from "@/pages/NewProject";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PublicDeck from "@/pages/PublicDeck";
import Journey from "@/pages/Journey";
import Help from "@/pages/Help";
import Admin from "@/pages/Admin";
import IdeaGenerator from "@/pages/IdeaGenerator";
import About from "@/pages/About";
import AdCreator from "@/pages/AdCreator";
import Layout from "@/components/Layout";

function ProtectedRoute({ children }) {
  const { user, ready, uiMode } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Guided Mode is a self-contained journey — no other route is reachable until they switch.
  if (uiMode === "guided") return <Navigate to="/journey" replace />;
  return <Layout>{children}</Layout>;
}

function JourneyRoute() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Journey />;
}

function OnboardingRoute() {
  const { user, ready, hasProfile } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (hasProfile === true) return <Navigate to="/dashboard" replace />;
  return <Onboarding />;
}

function PublicOnly({ children, to = "/dashboard" }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to={to} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/deck/:pid" element={<PublicDeck />} />
            <Route path="/idea-generator" element={<IdeaGenerator />} />
            <Route path="/about" element={<About />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly to="/journey"><Register /></PublicOnly>} />
            <Route path="/onboarding" element={<OnboardingRoute />} />
            <Route path="/journey" element={<JourneyRoute />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
            <Route path="/offer-profiles" element={<ProtectedRoute><OfferProfiles /></ProtectedRoute>} />
            <Route path="/offer-profiles/new" element={<ProtectedRoute><OfferProfileBuilder /></ProtectedRoute>} />
            <Route path="/offer-profiles/:pid" element={<ProtectedRoute><OfferProfileBuilder /></ProtectedRoute>} />
            <Route path="/pitch-test/:pid" element={<ProtectedRoute><PitchTest /></ProtectedRoute>} />
            <Route path="/brand" element={<ProtectedRoute><BrandProfile /></ProtectedRoute>} />
            <Route path="/campaigns/new" element={<ProtectedRoute><NewCampaign /></ProtectedRoute>} />
            <Route path="/income-mode" element={<ProtectedRoute><IncomeMode /></ProtectedRoute>} />
            <Route path="/projects/new" element={<ProtectedRoute><NewProject /></ProtectedRoute>} />
            <Route path="/campaigns/:cid" element={<ProtectedRoute><CampaignDetail /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/hooks" element={<ProtectedRoute><SmartHooks /></ProtectedRoute>} />
            <Route path="/ad-creator" element={<ProtectedRoute><AdCreator /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
